/**
 * Typed client for the frond API (services/api). All endpoints are relative
 * to `config.api.baseUrl` — nothing is ever hardcoded.
 */

import { FrondConfig } from "@frond/config";

export interface DocumentMeta {
  id: string;
  name: string;
  sizeBytes: number;
  revision: number;
  createdAt: string;
  updatedAt: string;
  /** Public share view (only when publishing is enabled). */
  published?: {
    enabled: boolean;
    token: string;
    url: string;
  };
}

export interface DocumentRef {
  id: string;
  meta: DocumentMeta;
  /** Presigned GET URL for the document body (S3). */
  downloadUrl: string;
  /** Presigned PUT URL to upload a new revision (S3). */
  uploadUrl: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class FrondClient {
  constructor(private config: FrondConfig, private getToken: () => Promise<string | null>) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(`${this.config.api.baseUrl}${path}`, { ...init, headers });
    if (!res.ok) {
      let code = "unknown";
      let message = `Request failed (${res.status})`;
      try {
        const body = (await res.json()) as { code?: string; message?: string };
        code = body.code ?? code;
        message = body.message ?? message;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, code, message);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  /* ------------------------------ documents ------------------------------ */

  listDocuments(): Promise<{ items: DocumentMeta[] }> {
    return this.request("/documents");
  }

  /** Create a document; returns its S3 upload URL for the first revision. */
  createDocument(name: string): Promise<DocumentRef> {
    return this.request("/documents", { method: "POST", body: JSON.stringify({ name }) });
  }

  getDocument(id: string): Promise<DocumentRef> {
    return this.request(`/documents/${encodeURIComponent(id)}`);
  }

  updateDocumentMeta(
    id: string,
    patch: { name?: string },
  ): Promise<DocumentMeta> {
    return this.request(`/documents/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }

  deleteDocument(id: string): Promise<void> {
    return this.request(`/documents/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  /** Commit a revision after uploading the body to the S3 PUT url. */
  commitRevision(id: string, revision: number): Promise<DocumentMeta> {
    return this.request(`/documents/${encodeURIComponent(id)}/revisions`, {
      method: "POST",
      body: JSON.stringify({ revision }),
    });
  }

  /* ------------------------------- sharing ------------------------------- */

  publish(id: string, enabled: boolean): Promise<DocumentMeta> {
    return this.request(`/documents/${encodeURIComponent(id)}/publish`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    });
  }

  getPublished(token: string): Promise<DocumentRef> {
    return this.request(`/published/${encodeURIComponent(token)}`);
  }

  /** Helper to resolve the appropriate OAuth redirect URI (detecting localhost dev). */
  resolveRedirectUri(override?: string): string {
    if (override) return override;
    if (typeof window !== "undefined" && window.location) {
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1") {
        return `${window.location.origin}/callback`;
      }
    }
    return this.config.auth.redirectUri;
  }

  /* ------------------------------- auth ---------------------------------- */

  /** Exchange an OAuth authorization code for tokens (PKCE, hosted UI). */
  async exchangeCode(code: string, codeVerifier: string, redirectUri?: string): Promise<AuthTokens> {
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.config.auth.userPoolClientId,
      code,
      code_verifier: codeVerifier,
      redirect_uri: this.resolveRedirectUri(redirectUri),
    });
    const res = await fetch(`${this.config.auth.issuer}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!res.ok) throw new ApiError(res.status, "auth", "Token exchange failed");
    return (await res.json()) as AuthTokens;
  }

  async refreshTokens(refreshToken: string): Promise<AuthTokens> {
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: this.config.auth.userPoolClientId,
      refresh_token: refreshToken,
    });
    const res = await fetch(`${this.config.auth.issuer}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!res.ok) throw new ApiError(res.status, "auth", "Token refresh failed");
    return (await res.json()) as AuthTokens;
  }

  /** Sign out and redirect to the hosted UI's logout endpoint. */
  signOut(returnTo?: string): void {
    const base = `${this.config.auth.issuer}/logout`;
    const params = new URLSearchParams({
      client_id: this.config.auth.userPoolClientId,
      logout_uri: this.resolveRedirectUri(returnTo),
    });
    window.location.assign(`${base}?${params.toString()}`);
  }

  /** URL to start the hosted-UI authorization (sign in) flow (PKCE). */
  authorizeUrl(codeChallenge: string, redirectUri?: string): string {
    const params = new URLSearchParams({
      client_id: this.config.auth.userPoolClientId,
      response_type: "code",
      scope: this.config.auth.scopes.join(" "),
      redirect_uri: this.resolveRedirectUri(redirectUri),
      code_challenge_method: "S256",
      code_challenge: codeChallenge,
    });
    return `${this.config.auth.issuer}/oauth2/authorize?${params.toString()}`;
  }

  /** URL to start the hosted-UI registration (sign up) flow (PKCE). */
  signUpUrl(codeChallenge: string, redirectUri?: string): string {
    const params = new URLSearchParams({
      client_id: this.config.auth.userPoolClientId,
      response_type: "code",
      scope: this.config.auth.scopes.join(" "),
      redirect_uri: this.resolveRedirectUri(redirectUri),
      code_challenge_method: "S256",
      code_challenge: codeChallenge,
    });
    return `${this.config.auth.issuer}/signup?${params.toString()}`;
  }
}

export interface AuthCallbackResult {
  code: string | null;
  error: string | null;
  errorDescription: string | null;
}

export function parseAuthCallback(search: string): AuthCallbackResult {
  const params = new URLSearchParams(search);
  return {
    code: params.get("code"),
    error: params.get("error"),
    errorDescription: params.get("error_description"),
  };
}

export interface AuthTokens {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/* ------------------------------- PKCE utils ------------------------------ */

export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(48);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
